import React, { FC, useCallback } from 'react';

import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';

import UploadImage from 'components/UploadImage/UploadImage';

import { GeneralThemeFormValues } from '../../GeneralSettings/generalTheme.types';

import { FormField } from './FormField';

type GeneralEssentialsFormValues = Pick<
  GeneralThemeFormValues,
  'storeTabName' | 'loaderAnimation' | 'favicon' | 'storeSaleImage'
> & {
  defaultPlayerImage: string;
};

type GeneralEssentialsImageField =
  | 'loaderAnimation'
  | 'defaultPlayerImage'
  | 'favicon'
  | 'storeSaleImage';

type GeneralEssentialsSectionFieldsProps = {
  isProductMadness: boolean;
};

export const GeneralEssentialsSectionFields: FC<
  GeneralEssentialsSectionFieldsProps
> = ({ isProductMadness }) => {
  const {
    values,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    errors,
    touched
  } = useFormikContext<GeneralEssentialsFormValues>();

  const onEssentialImageUploaded = useCallback(
    (field: GeneralEssentialsImageField, url: string) => {
      setFieldValue(field, url);
      setFieldTouched(field, true);
    },
    [setFieldValue, setFieldTouched]
  );

  return (
    <>
      <FormField
        className="mb-4 [&_input:not([aria-invalid])]:border-zinc-200 [&_input]:placeholder:text-base-mutedForeground"
        label="Store Tab Title"
        name="storeTabName"
        value={values.storeTabName}
        onChange={handleChange}
        onBlur={handleBlur}
        required
        error={touched.storeTabName && Boolean(errors.storeTabName)}
        helperText={
          touched.storeTabName ? errors.storeTabName?.toString() : undefined
        }
        placeholder="Game Store"
        dataTestId="storeTabName"
      />
      <div className="grid grid-cols-1 gap-y-9 md:grid-cols-2 md:gap-x-9 md:col-span-2">
        <UploadImage
          title="Loader Animation"
          description="Lottie JSON — recommended for loading states"
          buttonText="Upload"
          onUpload={(url) => onEssentialImageUploaded('loaderAnimation', url)}
          selectedImageUrl={values.loaderAnimation || ''}
          forceType={EAssetType.ANIMATION}
          variant="storeSettings"
          className="w-full"
          data-testid="loaderAnimation"
          buttonDataTestId="uploadLoaderAnimation"
        />
        <UploadImage
          title="Default Player Profile"
          description="Image for the default player avatar"
          buttonText="Upload"
          onUpload={(url) => onEssentialImageUploaded('defaultPlayerImage', url)}
          selectedImageUrl={values.defaultPlayerImage || ''}
          forceType={EAssetType.PLAYER_PROFILE_IMG}
          variant="storeSettings"
          className="w-full"
          data-testid="defaultPlayerImage"
          buttonDataTestId="uploadDefaultPlayerImage"
        />
        <div
          className={
            isProductMadness
              ? 'grid grid-cols-1 gap-y-9 md:col-span-2 md:grid-cols-2 md:gap-x-9'
              : 'contents'
          }
        >
          <UploadImage
            title="Favicon"
            description="ICO, PNG — browser tab icon"
            isRequired
            buttonText="Upload"
            onUpload={(url) => onEssentialImageUploaded('favicon', url)}
            selectedImageUrl={values.favicon || ''}
            forceType={EAssetType.FAVICON}
            variant="storeSettings"
            className="w-full"
            data-testid="favicon"
            buttonDataTestId="uploadFavicon"
          />
          {isProductMadness && (
            <UploadImage
              title="Store Sale"
              description="Image used for store sale display"
              buttonText="Upload"
              onUpload={(url) => onEssentialImageUploaded('storeSaleImage', url)}
              selectedImageUrl={values.storeSaleImage || ''}
              forceType={EAssetType.GENERAL}
              variant="storeSettings"
              className="w-full"
              data-testid="storeSaleImage"
              buttonDataTestId="uploadStoreSaleImage"
            />
          )}
        </div>
      </div>
    </>
  );
};
